import React, { useContext, useEffect, useState } from "react";
import LoginStep from "./LoginStep";
import SelectAddressStep from "./SelectAddressStep";
import OrderSummary from "./OrderSummary";
import CartItemsContext from "../../../context/cartItems/CartItemsContext";
import UserProfileContext from "../../../context/userProfile/UserProfileContext";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import CheckoutButton from "../../common/CheckoutButton/CheckoutButton";
import UserDataAPI from "../../../api/firebase/UserDataAPI";
import OrdersAPI from "../../../api/firebase/OrdersAPI";
import { useNavigate } from "react-router-dom";
import CouponsAPI from "../../../api/firebase/CouponsAPI";
import { serverTimestamp, Timestamp } from "firebase/firestore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import metaPixel from "../../../utils/analytics/metaPixel";

const PlaceOrder = ({ handleClose, items = [], appliedCoupon = null, shippingCost = 0 }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const couponsAPI = new CouponsAPI();
  const [user, setUser] = useState(null);
  const userProfileContext = useContext(UserProfileContext);
  const cartItemsContext = useContext(CartItemsContext);
  const mobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [activeStep, setActiveStep] = useState(0);
  const [loginCompleted, setLoginCompleted] = useState(false);
  const [addressCompleted, setAddressCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const cartItems = items;
  const [selAddress, setSelAddress] = useState(null);
  const [currentOrder, setCurrentOrder] = useState(null);
  const shippingAmount = Number(shippingCost) || 0;
  const couponDiscount = Number(appliedCoupon?.amount) || 0;
  const [codCharges, setCodCharges] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const finalAmount = Math.max(0, cartItems.reduce(
    (total, item) => total + (Number(item.listPrice) || 0) * (Number(item.quantity) || 1),
    0
  ) + shippingAmount + (Number(codCharges) || 0) - couponDiscount);
  const userDataApi = new UserDataAPI();
  const ordersAPI = new OrdersAPI();
  useEffect(() => {
    setLoading(false);
    if (userProfileContext.state?.id || userProfileContext.state?.uid) {
      // console.log("user:", userProfileContext.state);
      setCodCharges(
        userProfileContext.state.codCharges
          ? userProfileContext.state.codCharges
          : 0
      );
      setUser({ ...userProfileContext.state });
      setLoginCompleted(true);
    } else {
      setCodCharges(0);
      setUser(null);
      setLoginCompleted(false);
    }
  }, [userProfileContext.state]);

  const clearCompletedCart = () => {
    cartItemsContext.setState([]);
    localStorage.setItem("cartItems", JSON.stringify([]));
    userProfileContext.setState((prev) => prev?.id || prev?.uid ? {
      ...prev,
      cartItems: [],
      couponApplied: null,
      shippingCost: 0,
      codCharges: 0,
    } : prev);
  };

  const handleStep = (index) => {
    setActiveStep(index);
  };
  const handleCreateOrder = (props) => {
    console.log("🚀 handleCreateOrder called with props:", props);
    const { success, order } = props;
    if (!success) {
      console.error("❌ Order creation failed - success is false");
      return;
    }

    console.log("✅ Order creation successful, processing order:", order);

    const orderData = {
      id: order.id,
      amount: order.amount / 100,
      status: order.status,
      shippingCost: shippingAmount,
      couponApplied: userProfileContext.state.couponApplied
        ? userProfileContext.state.couponApplied
        : null,
      paymentMethod: process.env.REACT_APP_PAYMENT_METHOD || "LIVE",
      user: {
        id: user.id,
        name: user.displayName,
        email: user.email,
      },
      items: [...cartItems],
      address: selAddress,
      createdAt: serverTimestamp(),
    };

    console.log("📦 Order data prepared:", orderData);

    // Add current order in database
    const dataForOrdersTable = {
      id: orderData.id,
      payload: orderData,
    };

    console.log("🗄️ Adding order to orders table:", dataForOrdersTable);

    ordersAPI
      .setOrder(dataForOrdersTable)
      .then((resOrder) => {
        console.log("📊 Orders table response:", resOrder);
        if (!resOrder.success) {
          console.error(
            "❌ Failed to create order in orders collection:",
            resOrder.error
          );
          return;
        }

        console.log("✅ Order created in orders table with status CREATED.");

        // Add order to user's table with "Created" status so they can see pending orders
        const prevOrders = user.orders !== undefined ? user.orders : [];
        console.log("👤 Previous user orders:", prevOrders.length, "orders");

        const orderForUserTable = {
          ...orderData,
          // Firestore does not allow serverTimestamp() inside arrays. Use Timestamp.now() for users.orders entries.
          createdAt: Timestamp.now(),
          status: "Created", // Set status as "Created" initially
          payment: {
            method: "online",
            status: "pending",
          },
        };

        console.log("📝 Order for user table:", orderForUserTable);

        const dataForUsersTable = {
          id: user.id,
          payload: {
            orders: [orderForUserTable, ...prevOrders],
          },
        };

        console.log("👥 Data for users table:", dataForUsersTable);
        console.log("🔄 Calling userDataApi.addUserData...");

        userDataApi
          .addUserData(dataForUsersTable)
          .then((resUser) => {
            console.log("👤 Users table response:", resUser);
            if (resUser.success) {
              console.log(
                "✅ Order added to user's history with 'Created' status."
              );
              console.log("🔄 Updating user context...");
              // Update context to show the order in user's history
              userProfileContext.setState((prev) => ({
                ...prev,
                orders: dataForUsersTable.payload.orders,
              }));
              console.log("✅ User context updated successfully");

              // Verify the order was actually saved by fetching user data again
              setTimeout(() => {
                console.log(
                  "🔍 Verifying order was saved - fetching fresh user data..."
                );
                const authAPI =
                  new (require("../../../api/firebase/AuthAPI").default)();
                authAPI.getUserDetailById(user.id).then((freshUserData) => {
                  console.log(
                    "🔄 Fresh user data from database:",
                    freshUserData
                  );
                  console.log(
                    "📊 Fresh user orders count:",
                    freshUserData?.orders?.length || 0
                  );
                  if (freshUserData?.orders) {
                    const createdOrders = freshUserData.orders.filter(
                      (order) => order.status === "Created"
                    );
                    console.log(
                      "📦 Created orders in database:",
                      createdOrders.length
                    );
                    createdOrders.forEach((order, index) => {
                      console.log(
                        `✅ Created Order ${index + 1}: ID=${
                          order.id
                        }, Status=${order.status}`
                      );
                    });
                  }
                });
              }, 2000);
            } else {
              console.error(
                "❌ Failed to add order to user's history:",
                resUser.error
              );
            }
          })
          .catch((userUpdateError) => {
            console.error("💥 Error updating user data:", userUpdateError);
          });
      })
      .catch((orderCreationError) => {
        console.error("💥 Error creating order:", orderCreationError);
        // Consider showing error message to user
      });
    //
    setCurrentOrder({ ...orderData });
  };
  const sendMail = async (orderID = "") => {
    const url = "https://payments.inproveda.com/public/api/order_placed";
    const customerName = user.displayName;
    const customerEmail = user.email;
    orderID = currentOrder !== null ? currentOrder.id : orderID;
    const orderId = orderID;
    const tableHeader =
      "<table style='width: 100%;'>" +
      "<tr>" +
      "<th style='text-align:left'></th>" +
      "<th style='text-align:left'>Product</th>" +
      "<th style='text-align:left'>List Price</th>" +
      "<th style='text-align:left'>Total</th>" +
      "</tr>";
    let tableBody = "";
    cartItems.map((item, index) => {
      tableBody +=
        "<tr><td style='vertical-align: top;'>" +
        (index + 1) +
        ".</td><td style='padding-right:16px'>" +
        item.title +
        "  x " +
        item.quantity +
        " </td> <td>₹" +
        item.listPrice +
        "</td><td>₹" +
        item.listPrice * item.quantity +
        "</td></tr>";
    });
    // Calculate final amount based on ppu and quantity of OrderItemsArr
    const totalItemsAmount = cartItems.reduce(function (
      accumulator,
      currentValue
    ) {
      return accumulator + currentValue.listPrice * currentValue.quantity;
    },
    0);
    const tableShippingAmount =
      "<tr><td></td><td></td><td>Shipping</td><td>" +
      (shippingAmount === 0 ? "Free" : `₹${shippingAmount}`) +
      "</td></tr>";
    let tableCODCharges = "";
    if (codCharges > 0) {
      tableCODCharges =
        `<tr><td></td><td></td><td>COD Charges</td><td>+ ₹` +
        codCharges +
        "</td></tr>";
    }
    let tableCouponDiscount = "";
    if (couponDiscount > 0) {
      tableCouponDiscount =
        `<tr><td></td><td></td><td>Coupon (${userProfileContext.state.couponApplied.couponCode})</td><td>- ₹` +
        couponDiscount +
        "</td></tr>";
    }

    const tableFinalAmount =
      "<tr><td></td><td></td><td>Final Amount</td><td>₹" +
      (totalItemsAmount + codCharges + shippingAmount - couponDiscount) +
      "</td></tr>";
    const orderSummaryTable =
      tableHeader +
      tableBody +
      tableShippingAmount +
      tableCODCharges +
      tableCouponDiscount +
      tableFinalAmount +
      "</table>";
    const nameTrimmed = selAddress.name.replace(/[^a-zA-Z0-9,()-+ ]/g, "");
    const addressTrimmed = selAddress.address.replace(
      /[^a-zA-Z0-9,()-+ ]/g,
      ""
    );
    const cityTrimmed = selAddress.city.replace(/[^a-zA-Z0-9,()-+ ]/g, "");
    const pincodeTrimmed = selAddress.pincode.replace(
      /[^a-zA-Z0-9,()-+ ]/g,
      ""
    );
    const landmarkTrimmed = selAddress.landmark.replace(
      /[^a-zA-Z0-9,() ]/g,
      ""
    );
    const shippingAddressData =
      "<div><p>" +
      nameTrimmed +
      "</p>" +
      "<p>" +
      selAddress.mobile +
      "," +
      selAddress.alternateMobile +
      "</p><p>Address: " +
      addressTrimmed +
      "</p><p>City: " +
      cityTrimmed +
      "</p><p>State: " +
      selAddress.state +
      "</p><p>Pincode: " +
      pincodeTrimmed +
      "</p><p>Landmark: " +
      landmarkTrimmed +
      "</p></div>";
    // console.log("orderSummaryTable:", orderSummaryTable);
    // console.log("shippingAddress:", shippingAddressData);
    const options = JSON.stringify({
      cust_name: customerName,
      cust_email: customerEmail,
      from_name: "Inproveda Orders",
      from_email: "orders@inproveda.com",
      bcc_name: "Arpit Goyal",
      bcc_email: "arpitgoyal138@gmail.com",
      order_id: orderId,
      brand_name: "Inproveda",
      order_summary: orderSummaryTable,
      shipping_address: shippingAddressData,
    });
    try {
      await fetch(url, {
        method: "POST",
        body: options,
        headers: {
          "content-type": "application/json",
        },
      })
        .then((response) => {
          response.json();
        })
        .then((data) => {
          //console.log("data:", data);
        });
    } catch (error) {
      console.log("Server Error:", error);
    }
  };
  const handlePaymentUpdate = (props) => {
    console.log(
      "handlePaymentUpdate props:",
      props,
      "__currentOrder:",
      currentOrder,
      "__user:",
      user
    );

    const { success, response } = props;
    if (success) {
      sendMail();
      // Payment Completed
      const payment_id = response.razorpay_payment_id;
      const dataForOrderTable = {
        payload: {
          payment: {
            method: "online",
            status: "completed",
            payment_id: payment_id,
            attempted_at: Date().toString(),
          },
          status: "Placed",
        },
        id: currentOrder.id,
      };
      ordersAPI.setOrder(dataForOrderTable).then((resOrder) => {
        // console.log("RES ORDER:", resOrder);
        if (resOrder.success) {
          metaPixel.purchase({
            ...currentOrder,
            ...dataForOrderTable.payload,
          });
          clearCompletedCart();
          userDataApi.addUserData({
            id: user.id,
            payload: { cartItems: [], couponApplied: null, shippingCost: 0, codCharges: 0 },
          }).catch((error) => console.error("Failed to save cleared cart:", error));
          // navigate to order placed page
          navigate("/orderPlaced");
        }
      });
    } else {
      // Payment failed or modal dismissed
      const { dismissed } = props || {};
      console.log(
        "handlePaymentUpdate dismissed:",
        dismissed,
        currentOrder,
        user
      );
      if (dismissed && currentOrder && user) {
        try {
          // Mark this order as payment pending due to modal dismissal in user's orders
          const prevOrders = user.orders !== undefined ? user.orders : [];
          console.log("prevOrders:", prevOrders);

          // Check if current order exists in prevOrders
          const orderExists = prevOrders.some((o) => o.id === currentOrder.id);

          let updatedOrders;
          if (orderExists) {
            // Update existing order
            updatedOrders = prevOrders.map((o) =>
              o.id === currentOrder.id
                ? {
                    ...o,
                    status: o.status || "Created",
                    payment: {
                      ...(o.payment || {}),
                      method: (o.payment && o.payment.method) || "online",
                      status: "pending",
                      modalClosed: true,
                      attempted_at: Date().toString(),
                    },
                  }
                : o
            );
          } else {
            // Add current order with dismissal status (for first-time users)
            const { createdAt, ...orderWithoutTimestamp } = currentOrder;
            const orderForDismissal = {
              ...orderWithoutTimestamp,
              createdAt: Timestamp.now(), // Use regular timestamp instead of serverTimestamp
              payment: {
                method: "online",
                status: "pending",
                modalClosed: true,
                attempted_at: Date().toString(),
              },
            };
            updatedOrders = [orderForDismissal, ...prevOrders];
          }
          console.log("updatedOrders:", updatedOrders);
          const dataForUsersTable = {
            id: user.id,
            payload: {
              orders: updatedOrders,
            },
          };
          console.log("dataForUsersTable:", dataForUsersTable);
          userDataApi.addUserData(dataForUsersTable).then((resUser) => {
            if (resUser.success) {
              // Update context so UI reflects pending state immediately
              userProfileContext.setState((prev) => ({
                ...prev,
                orders: updatedOrders,
              }));
            } else {
              console.error(
                "Failed to mark order as payment pending (modal closed):",
                resUser.error
              );
            }
          });

          // Optionally record the attempt on the orders collection
          const dataForOrderTable = {
            payload: {
              payment: {
                method: "online",
                status: "pending",
                modalClosed: true,
                attempted_at: Date().toString(),
              },
              status: "Created",
            },
            id: currentOrder.id,
          };
          ordersAPI.setOrder(dataForOrderTable).then((resOrder) => {
            if (!resOrder.success) {
              console.warn(
                "Failed to update main order as pending after modal dismiss:",
                resOrder.error
              );
            }
          });
        } catch (e) {
          console.error("Error updating order to Payment Pending:", e);
        }
      } else {
        console.log("Payment Failed");
      }
    }
  };

  const handleCloseCOD = () => {
    setOpenDialog(false);
  };
  const handleAgreeCOD = () => {
    //console.log("user:", user);
    //handle confirm COD
    // generate random number of 8 characters based on timestamp
    const timeString = new Date().getTime().toString();
    let randNum = timeString.slice(-8);
    let randomOrderId = `COD${randNum}`;
    //console.log("timeString:", timeString, " ____ randomOrderId:", randomOrderId);
    const fAmount =
      cartItems.reduce((a, v) => (a = a + v.listPrice * v.quantity), 0) +
      shippingAmount +
      codCharges -
      couponDiscount;
    const codOrder = {
      id: randomOrderId,
      amount: fAmount,
      shippingCost: shippingAmount,
      codCharges: codCharges,
      couponApplied: userProfileContext.state.couponApplied
        ? userProfileContext.state.couponApplied
        : null,
      status: "Placed",
      payment: {
        method: "cod",
        status: "pending",
      },
      user: {
        id: user.id,
        name: user.displayName,
        email: user.email,
      },
      items: [...cartItems],
      address: selAddress,
      createdAt: serverTimestamp(),
      paymentMethod: process.env.REACT_APP_PAYMENT_METHOD || "LIVE",
    };
    //console.log("codOrder:", codOrder);
    const dataForOrderTable = {
      payload: {
        ...codOrder,
      },
      id: randomOrderId,
    };
    ordersAPI
      .setOrder(dataForOrderTable)
      .then((resOrder) => {
        // console.log("RES ORDER:", resOrder);
        if (!resOrder.success) {
          console.error(
            "Failed to create COD order in orders collection:",
            resOrder.error
          );
          // Don't proceed with user data update if order creation failed
          return;
        }

        const prevOrders = user.orders !== undefined ? user.orders : [];
        const dataForUsersTable = {
          id: user.id,
          payload: {
            cartItems: [], couponApplied: null, shippingCost: 0, codCharges: 0,
            // Avoid serverTimestamp inside arrays; override to Timestamp.now() for user-visible orders list
            orders: [
              { ...codOrder, createdAt: Timestamp.now() },
              ...prevOrders,
            ],
          },
        };
        // console.log("dataForUsersTable:", dataForUsersTable);
        userDataApi
          .addUserData(dataForUsersTable)
          .then((res) => {
            // console.log("RES from user data update:", res);
            if (res.success && resOrder.success) {
              clearCompletedCart();
              metaPixel.purchase(codOrder);
              // Only clear cart and update state if BOTH operations succeeded
              userProfileContext.setState((prev) => ({
                ...prev,
                cartItems: [],
                couponApplied: null,
                orders: dataForUsersTable.payload.orders,
              }));
            } else {
              console.error("Failed to update user orders for COD:", res.error);
              return;
            }
            //check if coupon applied
            if (
              user.couponApplied !== undefined &&
              user.couponApplied !== null
            ) {
              const couponPrevUsers = user.couponApplied.users
                ? user.couponApplied.users
                : [];
              const dataForCouponsTable = {
                payload: {
                  users: [...couponPrevUsers, user.id],
                },
                id: user.couponApplied.id,
              };
              couponsAPI
                .setCoupon(dataForCouponsTable)
                .then((res) => {
                  //console.log("Coupon updated");
                  sendMail(randomOrderId);
                  // navigate to order placed page
                  navigate("/orderPlaced");
                })
                .catch((err) => {
                  console.log("coupon update error:", err);
                });
            } else {
              sendMail(randomOrderId);
              // navigate to order placed page
              navigate("/orderPlaced");
            }
          })
          .catch((userUpdateError) => {
            console.error("Error updating user data for COD:", userUpdateError);
          });
      })
      .catch((orderCreationError) => {
        console.error("Error creating COD order:", orderCreationError);
        // Consider showing error message to user
      });
    setOpenDialog(false);
  };
  const steps = [
    {
      label: "Customer Info",
      component: <LoginStep user={user} loading={loading} />,
      isCompleted: loginCompleted,
    },
    {
      label: "Delivery Address",
      component: (
        <SelectAddressStep
          setCompleted={setAddressCompleted}
          selAddress={selAddress}
          setSelAddress={setSelAddress}
        />
      ),
      isCompleted: addressCompleted,
    },
    {
      label: "Order Review",
      component: (
        <OrderSummary
          items={cartItems}
          selAddress={selAddress}
          onEditAddress={() => setActiveStep(1)}
        />
      ),
      isCompleted: false,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 },
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#ffffff",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              bgcolor: "rgba(47, 93, 126, 0.1)",
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                lineHeight: 1.2,
                color: "#0f172a",
              }}
            >
              Secure Checkout
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem" }}>
              256-Bit SSL Encrypted &bull; Razorpay Verified
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: {xs: "block", sm: "none"},
                mt: 0.25,
                color: "primary.main",
                fontWeight: 800,
                fontSize: "0.66rem",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Step {activeStep + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: "#64748b",
            bgcolor: "#f8fafc",
            border: "1px solid #f1f5f9",
            "&:hover": { bgcolor: "#f1f5f9", color: "#0f172a" },
          }}
          aria-label="Close checkout"
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Modern Interactive Step Progress Bar */}
      <Box
        sx={{
          py: {xs: 1, sm: 1.5},
          px: { xs: 1.5, sm: 3 },
          bgcolor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          flexShrink: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={{ xs: 1, sm: 2 }}
        >
          {steps.map((s, idx) => {
            const isCurrent = activeStep === idx;
            const isDone =
              (idx === 0 && loginCompleted) || (idx === 1 && addressCompleted);
            const canClick =
              idx < activeStep ||
              (idx === 1 && loginCompleted) ||
              (idx === 2 && loginCompleted && addressCompleted);

            return (
              <React.Fragment key={s.label}>
                <Box
                  onClick={() => canClick && setActiveStep(idx)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: canClick ? "pointer" : "default",
                    opacity: activeStep >= idx ? 1 : 0.6,
                    p: "4px 8px",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": canClick ? { bgcolor: "rgba(0,0,0,0.04)" } : {},
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      bgcolor: isCurrent
                        ? "primary.main"
                        : isDone
                        ? "#16a34a"
                        : "#cbd5e1",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: 800,
                      boxShadow: isCurrent
                        ? "0 2px 8px rgba(47, 93, 126, 0.3)"
                        : "none",
                    }}
                  >
                    {isDone && !isCurrent ? (
                      <CheckRoundedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      idx + 1
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isCurrent ? 800 : 600,
                      color: isCurrent
                        ? "primary.main"
                        : isDone
                        ? "#16a34a"
                        : "text.secondary",
                      fontSize: { xs: "0.78rem", sm: "0.85rem" },
                      display: {
                        xs: isCurrent ? "inline" : "none",
                        sm: "inline",
                      },
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>

                {idx < steps.length - 1 && (
                  <Box
                    sx={{
                      flexGrow: 1,
                      maxWidth: { xs: 24, sm: 50 },
                      height: 2,
                      bgcolor: activeStep > idx ? "primary.main" : "#cbd5e1",
                      transition: "all 0.3s ease",
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </Box>

      {/* Scrollable Step Body */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2.5 },
          overscrollBehavior: "contain",
          bgcolor: "#ffffff",
        }}
      >
        {steps[activeStep].component}
      </Box>

      {/* Sticky Bottom Action Bar */}
      <Box
        sx={{
          p: { xs: 1.25, sm: 2 },
          px: { xs: 2, sm: 3 },
          borderTop: "1px solid #e2e8f0",
          bgcolor: "#ffffff",
          boxShadow: "0 -4px 16px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: {xs: "column", sm: "row"},
          alignItems: {xs: "stretch", sm: "center"},
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        {/* Left: Total Amount Preview */}
        <Box sx={{display: {xs: "flex", sm: "block"}, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap"}}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              display: "block",
              letterSpacing: "0.04em",
              fontSize: "0.68rem",
            }}
          >
            TOTAL PAYABLE
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "primary.main", lineHeight: 1.2 }}
          >
            ₹{finalAmount}
          </Typography>
          {couponDiscount > 0 && (
            <Typography
              variant="caption"
              sx={{ color: "#16a34a", fontWeight: 700, display: "block", width: {xs: "100%", sm: "auto"}, mt: {xs: 0.35, sm: 0} }}
            >
              Saved ₹{couponDiscount} with coupon
            </Typography>
          )}
        </Box>

        {/* Right: Navigation / Pay Buttons */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{width: {xs: "100%", sm: "auto"}}}>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              onClick={() => setActiveStep((prev) => prev - 1)}
              startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                borderColor: "#cbd5e1",
                color: "#475569",
                py: 1,
                px: 2,
                flex: {xs: 1, sm: "initial"},
                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
              }}
            >
              Back
            </Button>
          )}

          {activeStep === 0 && (
            <Button
              variant="contained"
              disabled={!loginCompleted}
              onClick={() => setActiveStep(1)}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 800,
                py: 1.1,
                px: 2.5,
                flex: {xs: 1, sm: "initial"},
              }}
            >
              Proceed to Address
            </Button>
          )}

          {activeStep === 1 && (
            <Button
              variant="contained"
              disabled={!addressCompleted}
              onClick={() => setActiveStep(2)}
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 800,
                py: 1.1,
                px: 2.5,
                flex: {xs: 1, sm: "initial"},
              }}
            >
              Review Order
            </Button>
          )}

          {activeStep === 2 && (
            <Box sx={{ minWidth: { xs: 0, sm: 200 }, flex: {xs: 1, sm: "initial"} }}>
              <CheckoutButton
                user={userProfileContext.state}
                amount={finalAmount}
                onCreateOrder={handleCreateOrder}
                currentOrder={currentOrder}
                onPayment={handlePaymentUpdate}
                disabled={cartItems.length === 0}
              />
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default PlaceOrder;
